import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users,
  User,
  Mail,
  Phone,
  MapPin,
  Building2,
  Briefcase,
  Calendar,
  TrendingUp,
  BarChart3,
  Target,
  Activity,
  Search,
  Filter,
  Plus,
  Eye,
  Edit,
  MessageSquare,
  Star,
  Clock,
  Tag,
  Download,
  Upload,
  UserPlus,
  UserCheck,
  UserX,
  Zap
} from 'lucide-react';
import { Button } from '../../../../components/ui/Button/Button';
import { Card } from '../../../../components/ui/Card';
import { Input } from '../../../../components/ui/Input';
import { cn } from '../../../../utils/cn';

interface ContactsTabProps {
  campaignData: any;
  onDataChange: (field: string, value: any) => void;
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  title: string;
  company: string;
  location: string;
  linkedinUrl?: string;
  tags: string[];
  status: 'new' | 'contacted' | 'engaged' | 'qualified' | 'customer' | 'unresponsive';
  source: 'manual' | 'import' | 'linkedin' | 'website' | 'referral';
  engagement: {
    emailsSent: number;
    emailsOpened: number;
    emailsClicked: number;
    emailsReplied: number;
    meetingsBooked: number;
    lastActivity: string;
    engagementScore: number;
  };
  demographics: {
    industry: string;
    companySize: string;
    seniority: string;
    department: string;
  };
  createdAt: string;
  updatedAt: string;
}

const mockContacts: Contact[] = [
  {
    id: '1',
    firstName: 'Sarah',
    lastName: 'Johnson',
    email: 'sarah.johnson@techcorp.com',
    phone: '+1 (555) 123-4567',
    title: 'VP of Engineering',
    company: 'TechCorp Solutions',
    location: 'San Francisco, CA',
    linkedinUrl: 'https://linkedin.com/in/sarahjohnson',
    tags: ['decision-maker', 'technical', 'high-priority'],
    status: 'qualified',
    source: 'linkedin',
    engagement: {
      emailsSent: 8,
      emailsOpened: 6,
      emailsClicked: 3,
      emailsReplied: 2,
      meetingsBooked: 1,
      lastActivity: '2 hours ago',
      engagementScore: 85
    },
    demographics: {
      industry: 'Software',
      companySize: '500-1000',
      seniority: 'VP',
      department: 'Engineering'
    },
    createdAt: '2024-01-15',
    updatedAt: '2024-01-20'
  },
  {
    id: '2',
    firstName: 'Michael',
    lastName: 'Chen',
    email: 'michael.chen@globaldynamics.com',
    title: 'Director of Operations',
    company: 'Global Dynamics Inc',
    location: 'Chicago, IL',
    tags: ['operations', 'process-improvement'],
    status: 'engaged',
    source: 'referral',
    engagement: {
      emailsSent: 5,
      emailsOpened: 4,
      emailsClicked: 2,
      emailsReplied: 1,
      meetingsBooked: 0,
      lastActivity: '1 day ago',
      engagementScore: 72
    },
    demographics: {
      industry: 'Manufacturing',
      companySize: '1000+',
      seniority: 'Director',
      department: 'Operations'
    },
    createdAt: '2024-01-10',
    updatedAt: '2024-01-18'
  }
];

const statusColors = {
  'new': 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300',
  'contacted': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  'engaged': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  'qualified': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  'customer': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  'unresponsive': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const sourceColors = {
  'manual': 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300',
  'import': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  'linkedin': 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  'website': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  'referral': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
};

export const ContactsTab: React.FC<ContactsTabProps> = ({ campaignData, onDataChange }) => {
  const [activeView, setActiveView] = useState<'overview' | 'contacts' | 'segments' | 'analytics'>('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);

  const getEngagementColor = (score: number) => {
    if (score >= 80) return 'text-green-600 dark:text-green-400';
    if (score >= 60) return 'text-blue-600 dark:text-blue-400';
    if (score >= 40) return 'text-orange-600 dark:text-orange-400';
    return 'text-red-600 dark:text-red-400';
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-6">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Total Contacts</p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">3,247</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600 dark:text-green-400">+18%</span>
            <span className="text-neutral-600 dark:text-neutral-400 ml-1">vs last month</span>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Qualified Leads</p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">234</p>
            </div>
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <Target className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600 dark:text-green-400">+32%</span>
            <span className="text-neutral-600 dark:text-neutral-400 ml-1">vs last month</span>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Avg Engagement</p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">67%</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <Activity className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600 dark:text-green-400">+8%</span>
            <span className="text-neutral-600 dark:text-neutral-400 ml-1">vs last month</span>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Response Rate</p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">12.4%</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600 dark:text-green-400">+4.2%</span>
            <span className="text-neutral-600 dark:text-neutral-400 ml-1">vs last month</span>
          </div>
        </Card>
      </div>

      {/* Top Contacts */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">High-Value Contacts</h3>
          <Button variant="outline" size="sm" onClick={() => setActiveView('contacts')}>
            View All
          </Button>
        </div>
        <div className="space-y-4">
          {mockContacts.slice(0, 3).map((contact) => (
            <div key={contact.id} className="flex items-center justify-between p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white dark:bg-neutral-700 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-neutral-600 dark:text-neutral-400" />
                </div>
                <div>
                  <h4 className="font-medium text-neutral-900 dark:text-neutral-100">
                    {contact.firstName} {contact.lastName}
                  </h4>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    {contact.title} at {contact.company}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <p className={cn("text-sm font-medium", getEngagementColor(contact.engagement.engagementScore))}>
                    {contact.engagement.engagementScore}% engaged
                  </p>
                  <p className="text-xs text-neutral-600 dark:text-neutral-400">
                    {contact.engagement.emailsReplied} replies
                  </p>
                </div>
                <span className={cn(
                  "px-2 py-1 rounded-full text-xs font-medium",
                  statusColors[contact.status]
                )}>
                  {contact.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );

  const renderContacts = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">All Contacts</h3>
        <div className="flex items-center space-x-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              placeholder="Search contacts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-64"
            />
          </div>
          <Button variant="outline" size="sm">
            <Filter className="w-4 h-4 mr-2" />
            Filter
          </Button>
          <Button variant="outline" size="sm">
            <Upload className="w-4 h-4 mr-2" />
            Import
          </Button>
          <Button variant="outline" size="sm">
            <Plus className="w-4 h-4 mr-2" />
            Add Contact
          </Button>
        </div>
      </div>

      <div className="grid gap-6">
        {mockContacts.map((contact) => (
          <Card key={contact.id} className="p-6">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start space-x-4">
                <div className="w-16 h-16 bg-neutral-100 dark:bg-neutral-800 rounded-full flex items-center justify-center">
                  <User className="w-8 h-8 text-neutral-600 dark:text-neutral-400" />
                </div>
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <h4 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
                      {contact.firstName} {contact.lastName}
                    </h4>
                    <div className="flex items-center space-x-1">
                      {contact.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <p className="text-neutral-600 dark:text-neutral-400 mb-2">{contact.title}</p>
                  <div className="flex items-center space-x-4 text-sm text-neutral-600 dark:text-neutral-400">
                    <span className="flex items-center">
                      <Building2 className="w-4 h-4 mr-1" />
                      {contact.company}
                    </span>
                    <span className="flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      {contact.location}
                    </span>
                    <span className="flex items-center">
                      <Mail className="w-4 h-4 mr-1" />
                      {contact.email}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className={cn(
                  "px-3 py-1 rounded-full text-sm font-medium",
                  sourceColors[contact.source]
                )}>
                  {contact.source}
                </span>
                <span className={cn(
                  "px-3 py-1 rounded-full text-sm font-medium",
                  statusColors[contact.status]
                )}>
                  {contact.status}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-6 gap-6 mb-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">{contact.engagement.emailsSent}</p>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">Emails Sent</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {contact.engagement.emailsSent > 0 ? ((contact.engagement.emailsOpened / contact.engagement.emailsSent) * 100).toFixed(1) : 0}%
                </p>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">Open Rate</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {contact.engagement.emailsSent > 0 ? ((contact.engagement.emailsClicked / contact.engagement.emailsSent) * 100).toFixed(1) : 0}%
                </p>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">Click Rate</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{contact.engagement.emailsReplied}</p>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">Replies</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{contact.engagement.meetingsBooked}</p>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">Meetings</p>
              </div>
              <div className="text-center">
                <p className={cn("text-2xl font-bold", getEngagementColor(contact.engagement.engagementScore))}>
                  {contact.engagement.engagementScore}
                </p>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">Engagement</p>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4 text-neutral-400" />
                <span className="text-sm text-neutral-600 dark:text-neutral-400">
                  Last activity: {contact.engagement.lastActivity}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Button variant="ghost" size="sm">
                  <Eye className="w-4 h-4 mr-2" />
                  View Profile
                </Button>
                <Button variant="ghost" size="sm">
                  <Mail className="w-4 h-4 mr-2" />
                  Send Email
                </Button>
                <Button variant="outline" size="sm">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderSegments = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Contact Segments</h3>
        <Button variant="outline" size="sm">
          <Plus className="w-4 h-4 mr-2" />
          Create Segment
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {[
          { name: 'High-Value Prospects', count: 234, criteria: 'Engagement Score > 70', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
          { name: 'Decision Makers', count: 156, criteria: 'Title contains VP, Director, C-Level', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
          { name: 'Tech Industry', count: 892, criteria: 'Industry = Software/Technology', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
          { name: 'Unresponsive', count: 445, criteria: 'No response in 30 days', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
          { name: 'Recent Signups', count: 67, criteria: 'Created in last 7 days', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
          { name: 'Enterprise Contacts', count: 123, criteria: 'Company size > 1000 employees', color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' },
        ].map((segment) => (
          <Card key={segment.name} className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">{segment.name}</h4>
              <span className={cn("px-3 py-1 rounded-full text-sm font-medium", segment.color)}>
                {segment.count} contacts
              </span>
            </div>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">{segment.criteria}</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Button variant="ghost" size="sm">
                  <Eye className="w-4 h-4 mr-2" />
                  View
                </Button>
                <Button variant="ghost" size="sm">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              </div>
              <Button variant="outline" size="sm">
                <Zap className="w-4 h-4 mr-2" />
                Start Campaign
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderAnalytics = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Contact Analytics</h3>
      
      <div className="grid grid-cols-2 gap-6">
        <Card className="p-6">
          <h4 className="text-md font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Contact Sources</h4>
          <div className="space-y-4">
            {Object.entries(sourceColors).map(([source, colorClass]) => (
              <div key={source} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={cn("w-3 h-3 rounded-full", colorClass.split(' ')[0])} />
                  <span className="text-sm text-neutral-900 dark:text-neutral-100 capitalize">{source}</span>
                </div>
                <span className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  {Math.floor(Math.random() * 30 + 10)}%
                </span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <h4 className="text-md font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Engagement Distribution</h4>
          <div className="space-y-4">
            {[
              { range: '80-100%', label: 'Highly Engaged', count: 234, color: 'bg-green-500' },
              { range: '60-79%', label: 'Moderately Engaged', count: 567, color: 'bg-blue-500' },
              { range: '40-59%', label: 'Low Engagement', count: 892, color: 'bg-orange-500' },
              { range: '0-39%', label: 'Unengaged', count: 445, color: 'bg-red-500' },
            ].map((item) => (
              <div key={item.range} className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-900 dark:text-neutral-100">{item.label} ({item.range})</span>
                  <span className="font-medium text-neutral-900 dark:text-neutral-100">{item.count}</span>
                </div>
                <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
                  <div 
                    className={cn("h-2 rounded-full", item.color)}
                    style={{ width: `${(item.count / 2138) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <h4 className="text-md font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Contact Lifecycle</h4>
        <div className="space-y-4">
          {[
            { stage: 'New Contacts', count: 3247, percentage: 100, color: 'bg-neutral-500' },
            { stage: 'Contacted', count: 2456, percentage: 75.6, color: 'bg-blue-500' },
            { stage: 'Engaged', count: 1234, percentage: 38.0, color: 'bg-green-500' },
            { stage: 'Qualified', count: 234, percentage: 7.2, color: 'bg-purple-500' },
            { stage: 'Customers', count: 67, percentage: 2.1, color: 'bg-emerald-500' }
          ].map((item) => (
            <div key={item.stage} className="flex items-center justify-between p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className={cn("w-4 h-4 rounded-full", item.color)} />
                <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{item.stage}</span>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-neutral-600 dark:text-neutral-400">{item.percentage}%</span>
                <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{item.count.toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex items-center space-x-1 bg-neutral-100 dark:bg-neutral-800 p-1 rounded-lg">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'contacts', label: 'Contacts', icon: Users },
          { id: 'segments', label: 'Segments', icon: Tag },
          { id: 'analytics', label: 'Analytics', icon: Activity }
        ].map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id as any)}
              className={cn(
                "flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200",
                activeView === tab.id
                  ? "bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 shadow-sm"
                  : "text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
              )}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      <motion.div
        key={activeView}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {activeView === 'overview' && renderOverview()}
        {activeView === 'contacts' && renderContacts()}
        {activeView === 'segments' && renderSegments()}
        {activeView === 'analytics' && renderAnalytics()}
      </motion.div>
    </div>
  );
}; 